import { useParams } from "react-router-dom";
import useAuth from "@/core/auth/use-auth";
import { useBusiness } from "@/core/business";
import { hasAiScriptAccess } from "@/core/business/payroll-only";
import { useEnv } from "@/core/env";
import { PAY_BREAKDOWN_RUN_TARGET_TYPE } from "@/payroll/pay-breakdown/types";
import { usePayrollNoteComposer } from "@/payroll/period-notes";
import { getPayBreakdownViewState } from "./pay-breakdown-view-state";
import { useAiWorkbookDownload } from "./use-ai-workbook-download";
import { useIcmOutput } from "./use-icm-output";
import { usePayBreakdownPanels } from "./use-pay-breakdown-panels";
import { usePayBreakdownPeriod } from "./use-pay-breakdown-period";
import { usePayBreakdownReviewState } from "./use-pay-breakdown-review-state";
import { usePayBreakdownRunActions } from "./use-pay-breakdown-run-actions";
import { usePayBreakdownSections } from "./use-pay-breakdown-sections";
import {
  getPayrollRunStep,
  usePayrollRunReveal,
} from "./use-payroll-run-reveal";

export const usePayBreakdownPage = () => {
  const { periodStart } = useParams<{ periodStart: string }>();
  const { businessId } = useBusiness();
  const { user } = useAuth();
  const { appEnv } = useEnv();
  const selectedPeriodStart = periodStart;

  const period = usePayBreakdownPeriod({
    businessId,
    user,
    selectedPeriodStart,
  });
  const reviewState = usePayBreakdownReviewState({
    businessId,
    user,
    business: period.business,
    aiScriptRunId: period.aiScriptRunId,
  });
  const icmOutput = useIcmOutput({
    businessId,
    user,
    business: period.business,
    output: period.output,
    hasActiveRun: period.hasActiveRun,
  });
  const workbookDownload = useAiWorkbookDownload({
    businessId,
    output: period.output,
    generatedWorkbookMetadata: period.generatedWorkbookMetadata,
  });
  const runActions = usePayBreakdownRunActions({
    ...period,
    businessId,
    hasAiScript: hasAiScriptAccess(period.business),
  });
  const viewState = getPayBreakdownViewState({
    ...period,
    statements: icmOutput.statements,
    isStatementsLoading: icmOutput.isStatementsLoading,
    runTarget: runActions.runTarget,
  });
  const runReveal = usePayrollRunReveal(
    getPayrollRunStep({
      showNoRunYet: viewState.showNoRunYet,
      showInitialCalculationState: viewState.showInitialCalculationState,
      isStartingRun:
        runActions.isCreatingAiRun || runActions.rerunAction.isRunning,
      hasActiveRun: period.hasActiveRun,
      activeRun: viewState.activeRun,
    }),
    period.output.type === PAY_BREAKDOWN_RUN_TARGET_TYPE.aiScript,
  );

  const sections = usePayBreakdownSections({
    businessId,
    user,
    selectedPeriodStart,
    business: period.business,
    output: period.output,
    isBusinessLoading: period.isBusinessLoading,
    isPayPeriodsLoading: period.isPayPeriodsLoading,
    selectedPayPeriod: period.selectedPayPeriod,
    payPeriods: period.payPeriods,
  });
  const canUseNotes =
    period.business?.can_rerun_full_payroll === true && !!selectedPeriodStart;
  const panels = usePayBreakdownPanels({
    output: period.output,
    aiScriptRunId: period.aiScriptRunId,
    activeSection: sections.activeSection,
    canUseNotes,
  });
  const noteComposer = usePayrollNoteComposer({
    businessId,
    periodStart: selectedPeriodStart,
    userId: user?.id,
    enabled: canUseNotes,
  });

  return {
    businessId,
    user,
    appEnv,
    selectedPeriodStart,
    ...period,
    canUseNotes,
    ...reviewState,
    ...icmOutput,
    ...sections,
    ...workbookDownload,
    ...runActions,
    ...panels,
    ...viewState,
    runReveal,
    noteComposer,
  };
};

export type PayBreakdownPageState = ReturnType<typeof usePayBreakdownPage>;
